// src/features/rooms/SelectedRoomsValidationsAgGrid.tsx
import "ag-grid-enterprise";
import React, { useMemo } from "react";
import { Box, Group, Text, Tooltip } from "@mantine/core";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { FaCheckCircle, FaExclamationTriangle, FaMinusCircle } from "react-icons/fa";

import { useAppSelector } from "../../../app/hooks";
import { selectSpaceValidationsMatrixForSelectedRoomsExcludingStandardRoom } from "./selectedRoomDevicesSelectors";

type RowVm = {
  roomNumber: string;
  roomName: string;
  hasDevices: boolean;
  allValid: boolean;
  [key: string]: any;
};

const StatusCell: React.FC<ICellRendererParams> = (props) => {
  const value = String(props.value ?? "");

  if (!value || value === "NOT_APPLICABLE") {
    return (
      <Group gap={6} wrap="nowrap">
        <FaMinusCircle size={15} color="#868e96" />
        <Text size="xs" c="dimmed">
          –
        </Text>
      </Group>
    );
  }

  if (value === "VALID") {
    return (
      <Group gap={6} wrap="nowrap">
        <FaCheckCircle size={15} color="#2f9e44" />
        <Text size="xs" c="green.7" fw={500}>
          OK
        </Text>
      </Group>
    );
  }

  // Alla andra fall (WARNING, ERROR, etc)
  return (
    <Group gap={6} wrap="nowrap">
      <FaExclamationTriangle size={15} color="#e03131" />
      <Text size="xs" c="red.7" fw={500}>
        Feil
      </Text>
    </Group>
  );
};

const RoomStatusCell: React.FC<ICellRendererParams> = (props) => {
  const hasDevices = Boolean(props.data?.hasDevices);
  const allValid = Boolean(props.data?.allValid);

  if (!hasDevices) {
    return null; // kommer inte att renderas pga vi filtrerar bort raderna
  }

  if (allValid) {
    return (
      <Group gap={6} wrap="nowrap">
        <FaCheckCircle size={16} color="#2f9e44" />
        <Text size="sm" c="green.8" fw={500}>
          Godkjent
        </Text>
      </Group>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      <FaExclamationTriangle size={16} color="#e03131" />
      <Text size="sm" c="red.8" fw={500}>
        Ikke godkjent
      </Text>
    </Group>
  );
};

export const SelectedRoomsValidationsAgGrid: React.FC = () => {
  const matrix = useAppSelector(selectSpaceValidationsMatrixForSelectedRoomsExcludingStandardRoom);

  const rowData = useMemo<RowVm[]>(() => {
    return (matrix.rows ?? [])
      .filter((r) => r.hasDevices) // ← VIKTIG ÄNDRING: bara rum med enheter
      .map((r) => ({
        roomName: r.roomName || "",
        roomNumber: r.roomNumber,
        hasDevices: r.hasDevices,
        allValid: r.allValid,
        ...r.statusByValidationId,
      }));
  }, [matrix.rows]);

  const columnDefs = useMemo<ColDef<RowVm>[]>(() => {
    const base: ColDef<RowVm>[] = [
      {
        headerName: "Romsnummer",
        field: "roomNumber",
        pinned: "left",
        width: 140,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Romnavn",
        field: "roomName",
        pinned: "left",
        width: 140,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Status",
        field: "allValid",
        pinned: "left",
        width: 170,
        sortable: true,
        filter: false,
        cellRenderer: RoomStatusCell,
        valueGetter: (p) => (p.data?.allValid ? "Godkjent" : "Ikke godkjent"),
      },
    ];

    const ruleCols: ColDef<RowVm>[] = (matrix.columns ?? []).map((c) => ({
      headerName: c.shortTitle ?? c.title,
      field: c.id as string,
      width: 170,
      sortable: false,
      filter: false,
      cellRenderer: StatusCell,
      headerTooltip: c.title,
      tooltipValueGetter: () => c.title,
      valueGetter: (p) => p.data?.[c.id] ?? "NOT_APPLICABLE",
    }));

    return [...base, ...ruleCols];
  }, [matrix.columns]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      suppressMovable: false,
    }),
    []
  );

  const summaryText = useMemo(() => {
    if (matrix.selectedRoomsCount === 0) return "Ingen rom valgt";

    const roomsWithDevices = matrix.roomsWithDevicesCount ?? rowData.length;
    const approved = matrix.approvedRoomsCount ?? rowData.filter((r) => r.allValid).length;
    const notApproved = roomsWithDevices - approved;

    if (roomsWithDevices === 0) {
      return `Valgte rom: ${matrix.selectedRoomsCount} (ingen har enheter)`;
    }

    return `Valgte rom: ${matrix.selectedRoomsCount} • Rom med enheter: ${roomsWithDevices} • Godkjent: ${approved} • Ikke godkjent: ${notApproved}`;
  }, [matrix, rowData]);

  if (matrix.selectedRoomsCount === 0 || rowData.length === 0) {
    return (
      <Box p="sm">
        <Text size="sm" c="dimmed">
          {matrix.selectedRoomsCount === 0
            ? "Ingen rom valgt."
            : "Ingen av de valgte rommene har enheter."}
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Group justify="space-between" align="center" mb="xs">
        <Tooltip
          label="Resultat för valda rum (standardrom-regeln är avstängd i denna vy)"
          withArrow
        >
          <Text size="sm" fw={600}>
            {summaryText}
          </Text>
        </Tooltip>
      </Group>

      <div className="ag-theme-alpine" style={{ height: 420, width: "100%" }}>
        <AgGridReact<RowVm>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows
          suppressRowClickSelection
          rowSelection="multiple"
          getRowId={(p) => p.data.roomNumber}
        />
      </div>
    </Box>
  );
};
